// components/empresa/EmpresaList.tsx
import React, { useState, useEffect } from 'react';
import { EmpresaService } from '../../services/empresaService';
import { Empresa } from '../../types/api';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { useNotifier } from '../ui/Notifier';

export const EmpresaList: React.FC = () => {
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredEmpresas, setFilteredEmpresas] = useState<Empresa[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const notifier = useNotifier();

  useEffect(() => {
    loadEmpresas();
  }, []);

  useEffect(() => {
    // Filtrar empresas quando o termo de busca mudar
    if (searchTerm.trim() === '') {
      setFilteredEmpresas(empresas);
    } else {
      const filtered = empresas.filter(empresa => 
        empresa.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        empresa.cnpj.includes(searchTerm) ||
        empresa.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredEmpresas(filtered);
    }
  }, [empresas, searchTerm]);

  const loadEmpresas = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await EmpresaService.list();
      setEmpresas(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id: string) => {
    setDeleteTargetId(id);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetId) return;
    setConfirmOpen(false);
    try {
      await EmpresaService.delete(deleteTargetId);
      notifier.show({ message: 'Empresa excluída com sucesso', severity: 'success' });
      await loadEmpresas();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir empresa');
    } finally {
      setDeleteTargetId(null);
    }
  };

  const handleUpdateSituacao = async (id: string, novaSituacao: string) => {
    try {
      await EmpresaService.updateSituacao(id, novaSituacao);
      await loadEmpresas();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao atualizar situação');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        <span className="ml-2 text-gray-600">Carregando empresas...</span>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-xs font-medium text-gray-900">
            Empresas ({filteredEmpresas?.length || 0})
          </h2>
          
          {/* Busca */}
          <div className="mt-4 sm:mt-0 sm:ml-4">
            <input
              type="text"
              placeholder="Buscar por nome, CNPJ ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-xs"
            />
          </div>
        </div>
      </div>

      {/* Erro */}
      {error && (
        <div className="p-4 bg-red-50 border-l-4 border-red-400">
          <div className="text-xs text-red-700">
            <strong>Erro:</strong> {error}
          </div>
        </div>
      )}

      {/* Lista */}
      {(filteredEmpresas?.length || 0) === 0 ? (
        <div className="p-8 text-center">
          <div className="text-gray-500">
            {searchTerm ? 'Nenhuma empresa encontrada com os critérios de busca.' : 'Nenhuma empresa cadastrada.'}
          </div>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="mt-2 text-indigo-600 hover:text-indigo-500 text-xs"
            >
              Limpar busca
            </button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Empresa
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CNPJ
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contato
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Situação
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(filteredEmpresas || []).map((empresa) => (
                <tr key={empresa.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-xs font-medium text-gray-900">
                      {empresa.nome}
                    </div>
                    {empresa.grupoId && (
                      <div className="text-xs text-gray-500">
                        Grupo: {empresa.grupoId}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-xs text-gray-900">
                      {EmpresaService.formatCnpj(empresa.cnpj)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-xs text-gray-900">
                      {empresa.email}
                    </div>
                    {empresa.telefone && (
                      <div className="text-xs text-gray-500">
                        {EmpresaService.formatPhone(empresa.telefone)}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={empresa.situacao}
                      onChange={(e) => handleUpdateSituacao(empresa.id, e.target.value)}
                      className="text-xs border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="Ativo">Ativo</option>
                      <option value="Inativo">Inativo</option>
                      <option value="Suspenso">Suspenso</option>
                      <option value="Cancelado">Cancelado</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-xs font-medium space-x-2">
                    <button
                      onClick={() => {
                        // TODO: Implementar edição
                        notifier.show({ message: 'Funcionalidade de edição será implementada em breve', severity: 'info' });
                      }}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleDelete(empresa.id)}
                      className="text-red-600 hover:text-red-900 ml-4"
                    >
                      Excluir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer com botão de recarregar */}
      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
        <button
          onClick={loadEmpresas}
          disabled={loading}
          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-xs leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
              Carregando...
            </>
          ) : (
            <>
              <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Recarregar
            </>
          )}
        </button>
      </div>

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Excluir Empresa"
        message="Tem certeza que deseja excluir esta empresa?"
        confirmText="Excluir"
        cancelText="Cancelar"
        confirmButtonColor="red"
        onConfirm={handleConfirmDelete}
        onCancel={() => { setConfirmOpen(false); setDeleteTargetId(null); }}
      />
    </div>
  );
};